import { Plane, ArrowLeft } from "lucide-react";

const CONTENT = {
  privacy: {
    eyebrow: "Legal",
    title: "Privacy Policy",
    intro:
      "The Ditch Collective respects your privacy and is committed to protecting your personal information.",
    sections: [
      {
        heading: "Information we may collect",
        body: "We may collect personal information including:",
        list: [
          "Name",
          "Email address",
          "Phone number",
          "Location information",
          "Employment details",
          "Relocation requirements",
          "Documents voluntarily provided by you",
        ],
      },
      {
        heading: "How we use your information",
        body: "Your information is collected solely for the purpose of providing relocation support, employment assistance, referrals, and customer service.",
      },
      {
        heading: "Sharing",
        body: "We do not sell or share personal information with third parties except where necessary to provide services requested by you, comply with legal obligations, or where you have provided consent.",
      },
      {
        heading: "Storage",
        body: "Reasonable steps are taken to ensure personal information is stored securely.",
      },
      {
        heading: "Consent",
        body: "By using our website or services, you consent to the collection and use of information in accordance with this Privacy Policy.",
      },
    ],
  },
  refund: {
    eyebrow: "Legal",
    title: "Refund Policy",
    intro:
      "At The Ditch Collective, we dedicate significant time, expertise, research, resources, planning, and support to each client.",
    sections: [
      {
        heading: "All sales are final",
        body: "All payments made for consultations, relocation packages, digital products, downloadable resources, and support services are non-refundable unless required by Australian Consumer Law.",
      },
      {
        heading: "Refunds will not be provided for",
        list: [
          "Change of mind",
          "Delayed relocation plans",
          "Employment outcomes",
          "Rental application outcomes",
          "Visa or government decisions",
          "Personal circumstances",
          "Failure to utilise purchased services",
          "Decisions not to proceed with relocation",
        ],
      },
      {
        heading: "Where services have commenced",
        body: "Where services have commenced, consultations have been delivered, or resources have been provided, no refund will be issued.",
      },
      {
        heading: "Exceptional circumstances",
        body: "The Ditch Collective reserves the right to assess exceptional circumstances on a case-by-case basis.",
      },
    ],
  },
  disclaimer: {
    eyebrow: "Legal",
    title: "Disclaimer",
    intro:
      "The Ditch Collective provides relocation support, information, resources, referrals, and employment assistance for individuals moving from New Zealand to Australia.",
    sections: [
      {
        heading: "General information only",
        body: "The information provided on this website, through consultations, downloadable resources, emails, social media, and other communications is general information only and should not be relied upon as legal, migration, financial, taxation, accounting, lending, property, or professional advice.",
      },
      {
        heading: "Accuracy and changes",
        body: "While we make every effort to ensure information is accurate and current, laws, regulations, government policies, fees, and requirements may change without notice.",
      },
      {
        heading: "Seek independent advice",
        body: "Clients should seek independent professional advice relevant to their individual circumstances before making decisions relating to employment, taxation, healthcare, migration, property, finance, insurance, education, or relocation matters.",
      },
      {
        heading: "Liability",
        body: "The Ditch Collective accepts no liability for any loss, damage, cost, or inconvenience arising from reliance on information provided through our services, website, or resources.",
      },
      {
        heading: "Acceptance",
        body: "Use of this website constitutes acceptance of this disclaimer.",
      },
    ],
  },
  affiliate: {
    eyebrow: "Legal",
    title: "Affiliate Disclosure",
    intro:
      "The Ditch Collective may receive a commission, referral fee, promotional benefit, or other compensation from certain partners and service providers featured on our website. This comes at no additional cost to you.",
    sections: [
      {
        heading: "Why we partner",
        body: "We only promote products and services that we believe may provide value to our community; however, clients should undertake their own research and determine whether a product or service is suitable for their individual circumstances.",
      },
    ],
  },
  "third-party": {
    eyebrow: "Legal",
    title: "Third-Party Provider Disclaimer",
    intro:
      "The Ditch Collective may recommend, introduce, refer, or provide information regarding third-party businesses, employers, contractors, service providers, agents, brokers, consultants, and suppliers for the convenience of our clients.",
    sections: [
      {
        heading: "Third parties may include, but are not limited to",
        list: [
          "Employers and recruitment agencies",
          "Real estate agents and property managers",
          "Mortgage brokers and lenders",
          "Accountants and tax professionals",
          "Migration agents and legal professionals",
          "Insurance providers",
          "Removalists and transport providers",
          "Utility and telecommunications providers",
          "Education and training providers",
        ],
      },
      {
        heading: "No control over third parties",
        body: "The Ditch Collective does not own, operate, manage, supervise, or control any third-party provider and makes no representation, warranty, or guarantee regarding the quality, suitability, pricing, availability, conduct, performance, advice, products, or services provided by any third party.",
      },
      {
        heading: "Agreements are between you and the provider",
        body: "Any agreement, arrangement, contract, or transaction entered into with a third-party provider is solely between the client and the third-party provider.",
      },
      {
        heading: "Your due diligence",
        body: "Clients are responsible for conducting their own due diligence and obtaining independent advice before engaging any third-party provider.",
      },
      {
        heading: "Limitation of liability",
        body: "To the maximum extent permitted by law, The Ditch Collective accepts no liability for any loss, damage, expense, delay, dispute, injury, financial loss, employment outcome, accommodation outcome, service failure, or other consequence arising from or connected with the actions, omissions, advice, products, or services of any third-party provider.",
      },
      {
        heading: "Referrals are not endorsements",
        body: "The inclusion of a referral, recommendation, introduction, discount, affiliate partnership, promotional offer, or preferred supplier arrangement does not constitute an endorsement or guarantee by The Ditch Collective.",
      },
    ],
  },
};

export default function Legal({ kind = "privacy" }) {
  const doc = CONTENT[kind] || CONTENT.privacy;

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid={`legal-${kind}`}>
      <header className="px-5 md:px-8 py-5 border-b border-[var(--line)]">
        <a
          href="/"
          className="inline-flex items-center gap-2 font-display font-extrabold text-[var(--ink)]"
        >
          <Plane size={18} /> The Ditch Collective
        </a>
      </header>

      <main className="max-w-2xl mx-auto px-5 md:px-6 pt-12 pb-24">
        <a
          href="/"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-[var(--ocean)] hover:text-[var(--ink)] transition-colors"
          data-testid={`legal-${kind}-back`}
        >
          <ArrowLeft size={14} /> Back to site
        </a>

        <span className="num-tag text-xs mt-8 inline-block">{doc.eyebrow}</span>
        <h1
          className="font-display text-4xl sm:text-5xl font-extrabold mt-2 leading-tight"
          data-testid={`legal-${kind}-title`}
        >
          {doc.title}
        </h1>
        <p className="text-[var(--ocean)] mt-4 text-base leading-relaxed">{doc.intro}</p>

        <div className="mt-10 space-y-9">
          {doc.sections.map((s) => (
            <section key={s.heading}>
              <h2 className="font-display text-xl font-extrabold text-[var(--ink)]">
                {s.heading}
              </h2>
              {s.body && (
                <p className="text-[var(--ocean)] mt-3 text-[15px] leading-relaxed">
                  {s.body}
                </p>
              )}
              {s.list && (
                <ul className="mt-3 space-y-1.5 text-[15px] text-[var(--ocean)] leading-relaxed">
                  {s.list.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-[var(--lime-deep)] mt-1.5 shrink-0">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <p className="mt-14 text-xs text-[var(--ocean)]/70">
          Last updated: {new Date().toLocaleDateString("en-AU", { year: "numeric", month: "long" })}
          {" · "}
          Questions?{" "}
          <a
            href="mailto:hello@theditchcollective.co.nz"
            className="underline hover:text-[var(--ink)]"
          >
            hello@theditchcollective.co.nz
          </a>
        </p>
      </main>
    </div>
  );
}
